/**
 * UserManagementPage —— 用户授权（/governance/users）。
 *
 * 用户列表（用户名 / 显示名 / 状态 / 已有角色）+ 添加既有平台用户
 * + 授权抽屉（选角色 + 显式 Site / 组织子树 / 本人范围）
 * + 撤销授权 + 暂停 / 恢复当前 Site membership。
 *
 * 数据层统一走 governanceApi；权限门禁用 useAuth().hasPermission；颜色用 CSS 变量；无 emoji。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  WxbPageShell,
  WxbPageHeader,
  WxbButton,
  WxbDataTable,
  WxbTableActionCell,
  WxbTag,
  WxbBadge,
  WxbModal,
  WxbDrawer,
  WxbInput,
  WxbSelect,
  WxbTreeSelect,
  WxbSpinner,
  WxbEmpty,
  WxbTextarea,
  WxbPopconfirm,
} from '../../components/wxb-ui';
import type { WxbTagColor } from '../../components/wxb-ui/Tag/Tag';
import { wxbToast } from '../../components/wxb-ui/Toast/Toast';
import { useAuth } from '../../contexts/AuthContext';
import {
  governanceApi,
  UserSummary,
  UserStatus,
  RoleListItem,
  RoleAssignmentView,
  OrgUnitNode,
  GrantScopeType,
} from '../../services/governanceApi';
import './UserManagementPage.css';

const STATUS_META: Record<UserStatus, { label: string; color: WxbTagColor }> = {
  ACTIVE: { label: '启用', color: 'green' },
  INACTIVE: { label: '停用', color: 'amber' },
  LOCKED: { label: '锁定', color: 'red' },
  RETIRED: { label: '已退役', color: 'neutral' },
};

const UNIT_TYPE_LABELS: Record<string, string> = {
  DEPARTMENT: '部门',
  TEAM: '团队',
  GROUP: '组',
  SHIFT: '班次',
};

const SCOPE_TYPE_OPTIONS: Array<{ label: string; value: GrantScopeType }> = [
  { label: 'Site 全域', value: 'SITE' },
  { label: '组织子树', value: 'ORG_SUBTREE' },
  { label: '本人', value: 'SELF' },
];

const scopeLabel = (assignment: RoleAssignmentView): string => {
  if (assignment.scopeType === 'SITE') return 'Site 全域';
  if (assignment.scopeType === 'SELF') return '本人';
  return assignment.scopeUnitName
    ? `组织子树：${assignment.scopeUnitName}`
    : '组织子树';
};

const mapUserError = (err: unknown, fallback: string): string => {
  const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
  switch (code) {
    case 'MEMBERSHIP_EXISTS':
      return '该用户已经是当前 Site 的成员';
    case 'LAST_ADMIN_PROTECTED':
      return '不能撤销最后一个系统管理员的授权';
    case 'ROLE_PROTECTED':
      return '该角色受保护，无法执行此操作';
    case 'USER_NOT_FOUND':
      return '未找到可加入的有效平台账号，请联系平台管理员核对用户名';
    case 'ROLE_NOT_FOUND':
      return '角色不存在或已被删除';
    case 'DELEGATION_NOT_ALLOWED':
      return '不能授予超出你本人权限或有效期的角色';
    case 'BAD_REQUEST':
      return '请求参数不合法';
    default:
      return fallback;
  }
};

/** 把组织单元树转换成 AntD TreeSelect 的 treeData（顶部加「全局范围」选项）。 */
const toTreeData = (nodes: OrgUnitNode[]): any[] => {
  const mapNode = (node: OrgUnitNode): any => ({
    title: `${node.unitName}${UNIT_TYPE_LABELS[node.unitType] ? `（${UNIT_TYPE_LABELS[node.unitType]}）` : ''}`,
    value: String(node.id),
    selectable: node.isActive,
    children: node.children?.length ? node.children.map(mapNode) : undefined,
  });
  return nodes.map(mapNode);
};

/** 受保护角色码（与后端 PROTECTED_ROLE_CODE 一致）。 */
const PROTECTED_ROLE_CODE = 'GOVERNANCE_ADMIN';

const UserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission, hasPlatformPermission, user } = useAuth();
  const canWrite = hasPermission('GOVERNANCE_USER_WRITE');
  const canOperate = hasPermission('GOVERNANCE_USER_OPERATE');
  const canGrant = hasPermission('GOVERNANCE_ROLE_GRANT');
  const canCreatePlatformIdentity = hasPlatformPermission('PLATFORM_IDENTITY_CREATE');
  const currentUserId = user?.userId ?? null;

  const [users, setUsers] = useState<UserSummary[]>([]);
  const [roles, setRoles] = useState<RoleListItem[]>([]);
  const [orgUnits, setOrgUnits] = useState<OrgUnitNode[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  // 添加既有平台用户为当前 Site 成员
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [createUsername, setCreateUsername] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState<boolean>(false);

  // 授权抽屉
  const [grantUser, setGrantUser] = useState<UserSummary | null>(null);
  const [grantRoleId, setGrantRoleId] = useState<number | undefined>(undefined);
  const [grantScopeType, setGrantScopeType] = useState<GrantScopeType>('SITE');
  const [grantScopeUnitId, setGrantScopeUnitId] = useState<string | undefined>(undefined);
  const [grantEffectiveFrom, setGrantEffectiveFrom] = useState<string>('');
  const [grantEffectiveTo, setGrantEffectiveTo] = useState<string>('');
  const [grantReason, setGrantReason] = useState<string>('');
  const [granting, setGranting] = useState<boolean>(false);
  const [grantError, setGrantError] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await governanceApi.listUsers();
      setUsers(data);
    } catch (err) {
      setError(true);
      wxbToast.error('加载用户列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRolesAndOrg = useCallback(async () => {
    try {
      const [roleList, units] = await Promise.all([governanceApi.listRoles(), governanceApi.listOrgUnits()]);
      setRoles(roleList);
      setOrgUnits(units);
    } catch (err) {
      // 授权依赖项失败时，抽屉里会提示无可选项。
      setRoles([]);
      setOrgUnits([]);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadRolesAndOrg();
  }, [loadUsers, loadRolesAndOrg]);

  const activeRoleOptions = useMemo(
    () =>
      roles
        .filter((r) => r.roleStatus === 'ACTIVE')
        .map((r) => ({ label: `${r.roleName}（${r.roleCode}）`, value: r.id })),
    [roles],
  );

  /** 判断某用户是否持有活跃 GOVERNANCE_ADMIN 授权。 */
  const isActiveAdmin = useCallback(
    (u: UserSummary) =>
      u.roles.some((r) =>
        r.assignmentStatus === 'ACTIVE'
        && r.roleCode === PROTECTED_ROLE_CODE
        && r.scopeType === 'SITE',
      ),
    [],
  );

  /** 当前 Site 持有活跃 GOVERNANCE_ADMIN 的有效成员数。 */
  const activeAdminCount = useMemo(
    () => users.filter(
      (candidate) =>
        candidate.membershipStatus === 'ACTIVE' && isActiveAdmin(candidate),
    ).length,
    [users, isActiveAdmin],
  );

  const treeData = useMemo(() => toTreeData(orgUnits), [orgUnits]);

  const refreshGrantUser = useCallback(async (userId: number) => {
    try {
      const fresh = await governanceApi.getUser(userId);
      setGrantUser(fresh);
      setUsers((prev) => prev.map((u) => (u.id === userId ? fresh : u)));
    } catch {
      /* 刷新失败不阻塞 */
    }
  }, []);

  // ── 添加 Site 成员 ──
  const handleCreate = async () => {
    setCreateError(null);
    const username = createUsername.trim();
    if (!username) {
      setCreateError('请填写用户名');
      return;
    }
    setCreating(true);
    try {
      await governanceApi.addSiteMember({ username });
      wxbToast.success('Site 成员已添加');
      setCreateOpen(false);
      setCreateUsername('');
      loadUsers();
    } catch (err) {
      setCreateError(mapUserError(err, '创建用户失败'));
    } finally {
      setCreating(false);
    }
  };

  // ── Site membership 暂停 / 恢复 ──
  const handleSetMembership = useCallback(
    async (targetUser: UserSummary, status: 'ACTIVE' | 'SUSPENDED') => {
      try {
        await governanceApi.updateMembership(targetUser.id, status);
        wxbToast.success(status === 'ACTIVE' ? 'Site 成员关系已恢复' : 'Site 成员关系已暂停');
        loadUsers();
      } catch (err) {
        wxbToast.error(mapUserError(err, '更新 Site 成员关系失败'));
      }
    },
    [loadUsers],
  );

  // ── 授权抽屉 ──
  const openGrant = useCallback((user: UserSummary) => {
    setGrantUser(user);
    setGrantRoleId(undefined);
    setGrantScopeType('SITE');
    setGrantScopeUnitId(undefined);
    setGrantEffectiveFrom('');
    setGrantEffectiveTo('');
    setGrantReason('');
    setGrantError(null);
  }, []);

  const handleAssign = async () => {
    if (!grantUser) return;
    setGrantError(null);
    if (grantRoleId == null) {
      setGrantError('请选择要授予的角色');
      return;
    }
    if (grantScopeType === 'ORG_SUBTREE' && !grantScopeUnitId) {
      setGrantError('请选择组织子树的根节点');
      return;
    }
    if (
      grantEffectiveFrom
      && grantEffectiveTo
      && grantEffectiveTo <= grantEffectiveFrom
    ) {
      setGrantError('授权结束时间必须晚于开始时间');
      return;
    }
    setGranting(true);
    try {
      await governanceApi.assignRole(grantUser.id, {
        roleId: grantRoleId,
        scopeType: grantScopeType,
        scopeUnitId: grantScopeType === 'ORG_SUBTREE'
          ? Number(grantScopeUnitId)
          : null,
        effectiveFrom: grantEffectiveFrom
          ? grantEffectiveFrom.replace('T', ' ')
          : null,
        effectiveTo: grantEffectiveTo
          ? grantEffectiveTo.replace('T', ' ')
          : null,
        reasonText: grantReason.trim() || null,
      });
      wxbToast.success('授权已添加');
      setGrantRoleId(undefined);
      setGrantScopeType('SITE');
      setGrantScopeUnitId(undefined);
      setGrantEffectiveFrom('');
      setGrantEffectiveTo('');
      setGrantReason('');
      await refreshGrantUser(grantUser.id);
    } catch (err) {
      setGrantError(mapUserError(err, '授权失败'));
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = async (assignment: RoleAssignmentView) => {
    if (!grantUser) return;
    try {
      await governanceApi.revokeRoleAssignment(grantUser.id, assignment.assignmentId);
      wxbToast.success('授权已撤销');
      await refreshGrantUser(grantUser.id);
    } catch (err) {
      wxbToast.error(mapUserError(err, '撤销授权失败'));
    }
  };

  const columns = useMemo(
    () => [
      {
        title: '用户',
        dataIndex: 'displayName',
        key: 'displayName',
        render: (_: unknown, record: UserSummary) => (
          <div className="gv-user-name-cell">
            <span className="gv-user-display-name">{record.displayName}</span>
            <span className="gv-user-username">@{record.username}</span>
          </div>
        ),
      },
      {
        title: '邮箱',
        dataIndex: 'email',
        key: 'email',
        render: (value: string | null) =>
          value ? <span className="gv-secondary-text">{value}</span> : <span className="gv-muted-text">—</span>,
      },
      {
        title: '已有角色',
        key: 'roles',
        render: (_: unknown, record: UserSummary) => {
          const active = record.roles.filter((r) => r.assignmentStatus === 'ACTIVE');
          if (active.length === 0) {
            return <span className="gv-muted-text">未授权</span>;
          }
          return (
            <div className="gv-role-tags">
              {active.map((r) => (
                <WxbTag key={r.assignmentId} color="cyan">
                  {r.roleName}
                  {`·${scopeLabel(r)}`}
                </WxbTag>
              ))}
            </div>
          );
        },
      },
      {
        title: '当前 Site 状态',
        dataIndex: 'membershipStatus',
        key: 'membershipStatus',
        render: (_: unknown, record: UserSummary) => {
          if (record.userStatus !== 'ACTIVE') {
            const meta = STATUS_META[record.userStatus];
            return <WxbTag color={meta.color}>全局账号：{meta.label}</WxbTag>;
          }
          return record.membershipStatus === 'ACTIVE'
            ? <WxbTag color="green">有效成员</WxbTag>
            : <WxbTag color="amber">已暂停</WxbTag>;
        },
      },
      {
        title: '操作',
        key: 'actions',
        width: 220,
        render: (_: unknown, record: UserSummary) => {
          const actions = [];
          if (canGrant) {
            actions.push({ key: 'grant', label: '授权', onClick: () => openGrant(record) });
          }
          if (canOperate) {
            if (record.membershipStatus === 'ACTIVE') {
              // 防自锁 / 防暂停最后一个管理员（后端 LAST_ADMIN_PROTECTED 兜底，前端先置灰）。
              const isSelf = currentUserId != null && record.id === currentUserId;
              const isLastAdmin = isActiveAdmin(record) && activeAdminCount <= 1;
              const blockReason = isSelf
                ? '不能暂停自己的 Site membership'
                : isLastAdmin
                  ? '不能暂停当前 Site 最后一个系统管理员'
                  : undefined;
              const blocked = Boolean(blockReason);
              // blockReason 用于标签提示（置灰原因）；WxbTableActionItem 无 tooltip 字段，
              // 故把原因并入 label，hover 即可见。
              actions.push({
                key: 'suspend',
                label: blocked ? `暂停（${blockReason}）` : '暂停 Site 权限',
                onClick: () => handleSetMembership(record, 'SUSPENDED'),
                disabled: blocked,
                confirm: {
                  title: '确认暂停当前 Site 的成员关系？',
                  description: '暂停后该用户不能再访问当前 Site，其他 Site 不受影响。',
                },
              });
            } else {
              actions.push({
                key: 'reactivate',
                label: '恢复 Site 权限',
                onClick: () => handleSetMembership(record, 'ACTIVE'),
              });
            }
          }
          if (actions.length === 0) {
            return <span className="gv-muted-text">—</span>;
          }
          return <WxbTableActionCell actions={actions} maxInline={2} />;
        },
      },
    ],
    [canGrant, canOperate, handleSetMembership, openGrant, currentUserId, isActiveAdmin, activeAdminCount],
  );

  const activeGrantAssignments = useMemo(
    () => (grantUser ? grantUser.roles.filter((r) => r.assignmentStatus === 'ACTIVE') : []),
    [grantUser],
  );

  const renderBody = () => {
    if (loading) {
      return (
        <div className="gv-page-loading">
          <WxbSpinner tip="加载用户列表中…" />
        </div>
      );
    }
    return (
      <WxbDataTable<UserSummary>
        rowKey="id"
        columns={columns}
        dataSource={users}
        pagination={false}
        errorState={error ? { title: '加载失败', description: '用户列表加载失败' } : undefined}
        emptyState={{
          description: '暂无用户',
          action: canWrite ? (
            <WxbButton variant="secondary" onClick={() => setCreateOpen(true)}>
              添加成员
            </WxbButton>
          ) : undefined,
        }}
      />
    );
  };

  return (
    <WxbPageShell>
      <WxbPageHeader
        eyebrow="权限治理"
        title="用户授权"
        description="管理当前 Site 的成员关系与角色授权。暂停成员只影响当前 Site，不影响其他 Site。"
        meta={!loading && !error ? <WxbBadge variant="bar" status="info" label={`共 ${users.length} 个用户`} /> : undefined}
        actions={
          canWrite || canCreatePlatformIdentity ? (
            <>
              {canCreatePlatformIdentity ? (
                <WxbButton
                  variant="secondary"
                  onClick={() => navigate('/governance/identities')}
                >
                  新建平台用户
                </WxbButton>
              ) : null}
              {canWrite ? (
                <WxbButton variant="primary" onClick={() => setCreateOpen(true)}>
                  添加成员
                </WxbButton>
              ) : null}
            </>
          ) : undefined
        }
      />
      <div className="gv-page-content">{renderBody()}</div>

      {/* 添加既有平台用户 */}
      <WxbModal
        open={createOpen}
        title="添加 Site 成员"
        width={520}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        confirmLoading={creating}
        okText="添加"
      >
        <div className="gv-modal-fields">
          <WxbInput
            label="平台用户名"
            placeholder="输入已存在的准确登录用户名"
            value={createUsername}
            onChange={(e) => setCreateUsername(e.target.value)}
          />
          <div className="gv-modal-help">
            Site 管理员只能添加既有平台身份，不能修改全局账号状态或重置密码。
            {canCreatePlatformIdentity ? ' 如账号不存在，请先进入“平台用户”新建账号。' : ''}
          </div>
          {createError ? <div className="gv-form-error">{createError}</div> : null}
        </div>
      </WxbModal>

      {/* 授权抽屉 */}
      <WxbDrawer
        open={grantUser !== null}
        title={grantUser ? `授权 — ${grantUser.displayName}（@${grantUser.username}）` : '授权'}
        width={480}
        onClose={() => setGrantUser(null)}
        destroyOnClose
      >
        {grantUser ? (
          <div className="gv-grant-body">
            <section className="gv-grant-section">
              <h4 className="gv-grant-title">添加授权</h4>
              <div className="wxb-field">
                <label className="wxb-label">角色</label>
                <WxbSelect
                  placeholder={activeRoleOptions.length ? '选择角色' : '暂无可用角色'}
                  value={grantRoleId}
                  options={activeRoleOptions}
                  disabled={!canGrant || activeRoleOptions.length === 0}
                  onChange={(value) => setGrantRoleId(value as number)}
                  className="gv-full-width-control"
                  showSearch
                  optionFilterProp="label"
                />
              </div>
              <div className="wxb-field">
                <label className="wxb-label">授权范围</label>
                <WxbSelect
                  value={grantScopeType}
                  disabled={!canGrant}
                  options={SCOPE_TYPE_OPTIONS}
                  onChange={(value) => {
                    setGrantScopeType(value as GrantScopeType);
                    setGrantScopeUnitId(undefined);
                  }}
                  className="gv-full-width-control"
                />
                <span className="wxb-help">
                  Site 全域覆盖当前 Site；组织子树只覆盖所选节点及其后代；本人仅用于支持 SELF 的权限。
                </span>
              </div>
              {grantScopeType === 'ORG_SUBTREE' ? (
                <div className="wxb-field">
                  <label className="wxb-label">组织子树根节点</label>
                  <WxbTreeSelect
                    value={grantScopeUnitId}
                    disabled={!canGrant}
                    treeData={treeData}
                    onChange={(value) => setGrantScopeUnitId(value as string)}
                    treeDefaultExpandAll
                    className="gv-full-width-control"
                    placeholder="选择组织单元"
                  />
                </div>
              ) : null}
              <div className="gv-grant-effective-row">
                <WxbInput
                  label="生效时间（可选）"
                  type="datetime-local"
                  value={grantEffectiveFrom}
                  disabled={!canGrant}
                  onChange={(event) => setGrantEffectiveFrom(event.target.value)}
                />
                <WxbInput
                  label="结束时间（可选）"
                  type="datetime-local"
                  value={grantEffectiveTo}
                  disabled={!canGrant}
                  onChange={(event) => setGrantEffectiveTo(event.target.value)}
                />
              </div>
              <div className="wxb-field">
                <label className="wxb-label">授权原因（可选）</label>
                <WxbTextarea
                  rows={2}
                  placeholder="便于审计的授权说明"
                  value={grantReason}
                  disabled={!canGrant}
                  onChange={(e) => setGrantReason(e.target.value)}
                />
              </div>
              {grantError ? <div className="gv-form-error">{grantError}</div> : null}
              <WxbButton
                variant="primary"
                onClick={handleAssign}
                disabled={!canGrant || granting}
              >
                {granting ? '授权中…' : '添加授权'}
              </WxbButton>
            </section>

            <section className="gv-grant-section">
              <h4 className="gv-grant-title">当前授权（{activeGrantAssignments.length}）</h4>
              {activeGrantAssignments.length === 0 ? (
                <WxbEmpty description="该用户暂无生效授权" />
              ) : (
                <ul className="gv-grant-list">
                  {activeGrantAssignments.map((assignment) => (
                    <li className="gv-grant-item" key={assignment.assignmentId}>
                      <div className="gv-grant-item-main">
                        <span className="gv-grant-item-role">{assignment.roleName}</span>
                        <WxbTag color="cyan">{scopeLabel(assignment)}</WxbTag>
                      </div>
                      <span className="gv-grant-item-effective">
                        {assignment.effectiveFrom}
                        {' — '}
                        {assignment.effectiveTo ?? '持续有效'}
                      </span>
                      <WxbPopconfirm
                        title="确认撤销该授权？"
                        okText="撤销"
                        onConfirm={() => handleRevoke(assignment)}
                        disabled={!canGrant}
                      >
                        <WxbButton variant="ghost" size="sm" disabled={!canGrant}>
                          撤销
                        </WxbButton>
                      </WxbPopconfirm>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : null}
      </WxbDrawer>
    </WxbPageShell>
  );
};

export default UserManagementPage;
